"use client";

import React from "react";

import type { IActiveLocales, IEnv } from "~/types/api-types";
import type { SnakeToCamel } from "~/types/utility.types";
import { snakeToCamel } from "~/utils/snakeToCamel";

type CriticalEnv = IEnv & {
  current_locale_data: {
    locale_code: string;
    data: IActiveLocales;
  };
};

type NormalizedEnv = SnakeToCamel<CriticalEnv>;

type DataContextType = {
  env: NormalizedEnv;
};

type EnvDataContextType = DataContextType;

type EnvDataContextProviderProps = React.PropsWithChildren<{
  initialEnv: IEnv;
}>;

const EnvDataContext = React.createContext<EnvDataContextType | null>(null);

// Client side env context provider, env data must be preloaded in server side and passed here as prop
const EnvDataContextProvider = ({
  initialEnv,
  children,
}: EnvDataContextProviderProps) => {
  // No setter function because env is critical and has to be preloaded on server side
  const [env] = React.useState<NormalizedEnv>({
    ...snakeToCamel({
      ...initialEnv,
      current_locale_data: {
        data: initialEnv.active_locales[initialEnv.locale],
        locale_code: initialEnv.locale,
      },
    }),
  });

  return (
    <EnvDataContext.Provider value={{ env }}>
      {children}
    </EnvDataContext.Provider>
  );
};

export { EnvDataContext, EnvDataContextProvider };

export type { NormalizedEnv };
